/**
 * niftiRead.js — ЧТЕНИЕ NIfTI-1 ДЛЯ ВОЗВРАТА МАСКИ. [11.1.50]
 *
 * ЗАЧЕМ. Мост к чужой автосегментации. Своей сети у нас нет, а
 * TotalSegmentator в 3D Slicer размечает больше сотни органов за один
 * прогон и бесплатен. Путь становится таким: разметили там → сохранили
 * структуру в .nii → загрузили сюда в сегмент → дальше работает всё уже
 * своё: объём, «Только орган в 3D», STL, доводка кистью, отчёт.
 *
 * Второе применение, не менее важное: ПРОВЕРКА СВОЕЙ МАТЕМАТИКИ. Маска
 * от чужой программы против нашего счёта объёма — сошлось, значит счёт
 * верен; разошлось — видно, где именно врём.
 *
 * ПОЧЕМУ ЗДЕСЬ ТОЛЬКО ЧИСТЫЕ ФУНКЦИИ. Ни DOM, ни cornerstone: на входе
 * ArrayBuffer, на выходе числа и типизированный массив. Так разбор
 * проверяется прогоном без браузера — включая круг «наш экспорт → наш
 * импорт», где байты обязаны совпасть.
 *
 * ЧЕСТНАЯ ГРАНИЦА. Мы читаем РЕГУЛЯРНУЮ трёхмерную сетку: dim, pixdim,
 * datatype, vox_offset, scl_slope/inter. Матрицы поворота (sform/qform)
 * разбираются, но НЕ применяются: маска обязана лежать на той же сетке,
 * что и наш том, иначе её нельзя класть в сегмент — она уедет. Поэтому
 * вместо тихого пересчёта здесь сверка размеров и честный отказ словами.
 */

/** Коды типов данных NIfTI-1 → как читать. */
const ТИПЫ = Object.freeze({
  2: { имя: 'UINT8', байт: 1, читать: (dv, п) => dv.getUint8(п) },
  4: { имя: 'INT16', байт: 2, читать: (dv, п) => dv.getInt16(п, true) },
  8: { имя: 'INT32', байт: 4, читать: (dv, п) => dv.getInt32(п, true) },
  16: { имя: 'FLOAT32', байт: 4, читать: (dv, п) => dv.getFloat32(п, true) },
  64: { имя: 'FLOAT64', байт: 8, читать: (dv, п) => dv.getFloat64(п, true) },
  256: { имя: 'INT8', байт: 1, читать: (dv, п) => dv.getInt8(п) },
  512: { имя: 'UINT16', байт: 2, читать: (dv, п) => dv.getUint16(п, true) },
  768: { имя: 'UINT32', байт: 4, читать: (dv, п) => dv.getUint32(п, true) },
});

/** Сжат ли файл gzip (первые два байта 0x1f 0x8b). */
export function этоGzip(buffer) {
  if (!buffer || buffer.byteLength < 2) return false;
  const b = new Uint8Array(buffer, 0, 2);
  return b[0] === 0x1f && b[1] === 0x8b;
}

/**
 * Разобрать заголовок NIfTI-1.
 *
 * Порядок байт определяется по полю sizeof_hdr: оно обязано быть 348.
 * Если при чтении как little-endian вышло 1543569408 — файл записан
 * big-endian (это то же 348 задом наперёд), и читать надо иначе. Такие
 * файлы редки, но молча выдать из них кашу нельзя.
 *
 * @param {ArrayBuffer} buffer
 * @returns {{ок:true, ...}|{ок:false, причина:string}}
 */
export function разобратьЗаголовок(buffer) {
  // [11.1.50] Порядок проверок важен: сжатый файл может быть КОРОЧЕ 352
  // байт, и тогда прежний порядок выдавал «файл слишком мал» вместо
  // «файл сжат» — сообщение уводило бы в другую сторону.
  if (этоGzip(buffer)) {
    return { ок: false, причина: 'Файл сжат (.nii.gz) — его нужно распаковать перед разбором' };
  }
  if (!buffer || buffer.byteLength < 352) {
    return { ок: false, причина: 'Файл слишком мал для NIfTI (нужно минимум 352 байта)' };
  }
  const dv = new DataView(buffer);
  let LE = true;
  let sizeof_hdr = dv.getInt32(0, true);
  if (sizeof_hdr !== 348) {
    sizeof_hdr = dv.getInt32(0, false);
    LE = false;
  }
  if (sizeof_hdr !== 348) {
    return { ок: false, причина: 'Это не NIfTI-1: поле размера заголовка не равно 348' };
  }

  const magic = String.fromCharCode(dv.getUint8(344), dv.getUint8(345), dv.getUint8(346));
  if (magic !== 'n+1' && magic !== 'ni1') {
    return { ок: false, причина: `Это не NIfTI-1: подпись «${magic}» вместо «n+1»` };
  }
  if (magic === 'ni1') {
    return { ок: false, причина: 'Это парный формат .hdr/.img — нужен одиночный файл .nii' };
  }

  const ndim = dv.getInt16(40, LE);
  const nx = dv.getInt16(42, LE);
  const ny = dv.getInt16(44, LE);
  const nz = dv.getInt16(46, LE);
  const nt = ndim >= 4 ? dv.getInt16(48, LE) : 1;
  if (ndim < 3) return { ок: false, причина: `Маска должна быть объёмной, а в файле ${ndim} измерения` };
  if (nt > 1) return { ок: false, причина: `В файле ${nt} временных точек — для маски нужен один объём` };
  if (nx <= 0 || ny <= 0 || nz <= 0) return { ок: false, причина: 'Размеры в заголовке нечитаемы' };

  const код = dv.getInt16(70, LE);
  const тип = ТИПЫ[код];
  if (!тип) return { ок: false, причина: `Тип данных ${код} не поддерживается (нужен целочисленный или float)` };

  const sx = dv.getFloat32(80, LE);
  const sy = dv.getFloat32(84, LE);
  const sz = dv.getFloat32(88, LE);
  const vox_offset = Math.round(dv.getFloat32(108, LE)) || 352;
  let slope = dv.getFloat32(112, LE);
  const inter = dv.getFloat32(116, LE);
  // scl_slope = 0 по стандарту означает «масштабирования нет», а не «всё в ноль».
  if (!Number.isFinite(slope) || slope === 0) slope = 1;

  const нужно = vox_offset + nx * ny * nz * тип.байт;
  if (buffer.byteLength < нужно) {
    return { ок: false, причина: `Файл оборван: нужно ${нужно} байт, есть ${buffer.byteLength}` };
  }

  return {
    ок: true,
    dims: [nx, ny, nz],
    spacing: [Math.abs(sx) || 1, Math.abs(sy) || 1, Math.abs(sz) || 1],
    тип: тип.имя,
    код,
    vox_offset,
    slope,
    inter: Number.isFinite(inter) ? inter : 0,
    LE,
    вокселей: nx * ny * nz,
  };
}

/**
 * Прочитать воксели как МАСКУ: всё, что не ноль, становится индексом
 * сегмента. Именно так и надо: TotalSegmentator пишет 1, другие
 * программы — номер структуры, третьи — вероятность 0…1 во float.
 *
 * @param {ArrayBuffer} buffer
 * @param {number} индекс какой номер сегмента ставить
 * @param {{порог?:number}} о порог для дробных значений (по умолчанию 0.5)
 * @returns {{ок:true, пиксели:Uint8Array, dims, spacing, ненулевых:number, ...}|{ок:false, причина:string}}
 */
export function прочитатьМаску(buffer, индекс = 1, о = {}) {
  const з = разобратьЗаголовок(buffer);
  if (!з.ок) return з;
  const порог = Number.isFinite(Number(о.порог)) ? Number(о.порог) : 0.5;
  const тип = ТИПЫ[з.код];
  const dv = new DataView(buffer);
  const всего = з.вокселей;
  const пиксели = new Uint8Array(всего);
  let ненулевых = 0;
  let максЗначение = 0;
  for (let i = 0; i < всего; i++) {
    const сырое = тип.читать(dv, з.vox_offset + i * тип.байт);
    const v = сырое * з.slope + з.inter;
    if (Math.abs(v) > максЗначение) максЗначение = Math.abs(v);
    // Дробные значения (вероятности) — по порогу; целые — по «не ноль».
    if (v >= порог || (Number.isInteger(v) && v !== 0)) { пиксели[i] = индекс; ненулевых++; }
  }
  return {
    ок: true,
    пиксели,
    dims: з.dims,
    spacing: з.spacing,
    тип: з.тип,
    ненулевых,
    максЗначение,
    пусто: ненулевых === 0,
  };
}

/**
 * Ляжет ли маска на наш том. Сверяем РАЗМЕРЫ (это железно) и шаг вокселя
 * (это предупреждение: расхождение больше 1% значит другую серию либо
 * пересчитанную сетку, и объём по такой маске будет неправдой).
 *
 * @returns {{ок:boolean, причина?:string, тревога?:string}}
 */
export function сеткиСовпадают(dimsФайла, spacingФайла, dimsТома, spacingТома) {
  const d1 = (dimsФайла || []).map(Number);
  const d2 = (dimsТома || []).map(Number);
  if (d1.length !== 3 || d2.length !== 3 || d1.some((v) => !v) || d2.some((v) => !v)) {
    return { ок: false, причина: 'Размеры сетки не читаются' };
  }
  if (d1[0] !== d2[0] || d1[1] !== d2[1] || d1[2] !== d2[2]) {
    return {
      ок: false,
      причина: `Сетки разные: в файле ${d1.join('×')}, в открытом исследовании ${d2.join('×')}. `
        + 'Маска от другой серии или с другим шагом срезов — класть её нельзя, она ляжет мимо.',
    };
  }
  const s1 = (spacingФайла || []).map(Number);
  const s2 = (spacingТома || []).map(Number);
  let тревога = null;
  if (s1.length === 3 && s2.length === 3 && s1.every(Number.isFinite) && s2.every(Number.isFinite)) {
    const расх = [0, 1, 2].map((i) => (s2[i] ? Math.abs(s1[i] - s2[i]) / s2[i] : 0));
    const макс = Math.max(...расх);
    if (макс > 0.01) {
      тревога = `Шаг вокселя расходится на ${(макс * 100).toFixed(1)}%: `
        + `в файле ${s1.map((v) => v.toFixed(3)).join('×')} мм, в исследовании ${s2.map((v) => v.toFixed(3)).join('×')} мм. `
        + 'Размеры совпали, поэтому маска ляжет — но объём по ней считайте с осторожностью.';
    }
  }
  return { ок: true, тревога };
}

export default { этоGzip, разобратьЗаголовок, прочитатьМаску, сеткиСовпадают };
